import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  saveCompletedWorkout, 
  getCompletedWorkouts, 
  getExerciseHistory as getExerciseHistoryFromDB,
  saveExerciseProgress 
} from '../utils/workoutService';
import { subscribeToPush, schedulePushNotification, cancelPushNotification } from '../utils/pushService';
import { describeRun, runTargetText } from '../utils/runWorkout';

/**
 * WorkoutTrackerV2 - Premium, Minimal Workout Tracker
 * Clean, professional design for boutique fitness experience
 */

const WorkoutTrackerV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get workout and program info from navigation state
  const workoutFromNav = location.state?.workout;
  const programId = location.state?.programId;
  const weekNumber = location.state?.weekNumber;
  
  // Recover saved workout state (iOS kills PWAs when backgrounded)
  const savedState = (() => {
    try {
      const raw = localStorage.getItem('workoutInProgress');
      if (raw) {
        const s = JSON.parse(raw);
        // Only restore if it's the same workout and less than 3 hours old
        if (s.workoutTitle === workoutFromNav?.title && Date.now() - s.savedAt < 3 * 60 * 60 * 1000) {
          console.log('🔄 Restoring saved workout progress');
          return s;
        }
      }
    } catch (e) {}
    return null;
  })();

  // Workout state
  const [activeWorkout, setActiveWorkout] = useState(workoutFromNav || null);
  const [completedWorkouts, setCompletedWorkouts] = useState({});
  const [workoutStarted, setWorkoutStarted] = useState(true); // Auto-start workout immediately
  const [workoutStartTime, setWorkoutStartTime] = useState(savedState?.workoutStartTime || Date.now());
  const [workoutDuration, setWorkoutDuration] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  // Exercise progress
  const [completedSets, setCompletedSets] = useState(savedState?.completedSets || {});
  const [setData, setSetData] = useState(savedState?.setData || {});
  const [exerciseNotes, setExerciseNotes] = useState(savedState?.exerciseNotes || {});
  const [weightUnit, setWeightUnit] = useState(savedState?.weightUnit || 'kg');
  const [exerciseHistory, setExerciseHistory] = useState({});
  const [personalBests, setPersonalBests] = useState([]);

  // Run workout logging (for workoutType === 'run')
  const [runLog, setRunLog] = useState(savedState?.runLog || {
    distance: '',
    distanceUnit: workoutFromNav?.distanceUnit || 'km',
    rpe: '',
    notes: ''
  });
  const isRunWorkout = activeWorkout?.workoutType === 'run';
  
  // Timers
  const [restTimerActive, setRestTimerActive] = useState(false);
  const [restTimeRemaining, setRestTimeRemaining] = useState(0);
  const [restTimerEndTime, setRestTimerEndTime] = useState(null);
  
  // Audio context ref for timer sounds
  const audioContextRef = useRef(null);
  const noSleepVideoRef = useRef(null);
  const scheduledBeepRef = useRef(null); // For pre-scheduled beeps on iOS
  const timerSkippedRef = useRef(false); // Tracks if user skipped the timer
  const currentTimerIdRef = useRef(null); // Unique ID for current rest timer
  
  // Visual alert state (guaranteed fallback that always works)
  const [showRestOverAlert, setShowRestOverAlert] = useState(false);
  
  // Notification state
  const [notifPermission, setNotifPermission] = useState('default');
  const activeNotificationTimeoutRef = useRef(null); // Track scheduled notification timeout
  
  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotifPermission(Notification.permission);
    }
  }, []);
  
  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      alert('Notifications not supported on this device');
      return;
    }
    
    try {
      const permission = await Notification.requestPermission();
      setNotifPermission(permission);
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };
  
  // Keep audio context alive on EVERY user interaction (iOS suspends it otherwise)
  useEffect(() => {
    const keepAudioAlive = () => {
      try {
        if (!audioContextRef.current) {
          const AudioCtx = window.AudioContext || window.webkitAudioContext;
          audioContextRef.current = new AudioCtx();
        }
        if (audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }
      } catch (e) {}
    };

    document.addEventListener('touchstart', keepAudioAlive);
    document.addEventListener('click', keepAudioAlive);

    return () => {
      document.removeEventListener('touchstart', keepAudioAlive);
      document.removeEventListener('click', keepAudioAlive);
    };
  }, []);
  

  // Show visual alert when timer completes
  const playTimerSound = useCallback(() => {
    // If user skipped, do absolutely nothing
    if (timerSkippedRef.current) return;

    // Show visual alert only (no beeps, no vibration)
    setShowRestOverAlert(true);
  }, []);
  
  // UI state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);

  // Persist workout progress to localStorage (iOS kills PWAs when backgrounded)
  useEffect(() => {
    if (workoutStarted && activeWorkout) {
      try {
        localStorage.setItem('workoutInProgress', JSON.stringify({
          workoutTitle: activeWorkout.title,
          completedSets,
          setData,
          exerciseNotes,
          weightUnit,
          runLog,
          workoutStartTime,
          savedAt: Date.now(),
        }));
      } catch (e) {}
    }
  }, [completedSets, setData, exerciseNotes, weightUnit, runLog, workoutStarted, activeWorkout, workoutStartTime]);

  // Load workout data from Supabase on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    loadWorkoutData();
  }, []);

  const loadWorkoutData = async () => {
    const userEmail = localStorage.getItem('userEmail');
    console.log('🔍 Loading workout data for:', userEmail);
    
    if (!userEmail) {
      console.log('⚠️ No user email found, using localStorage');
      // Fallback to localStorage if no user email
      try {
        const localCompleted = JSON.parse(localStorage.getItem('completedWorkouts') || '{}');
        const localHistory = JSON.parse(localStorage.getItem('exerciseHistory') || '{}');
        console.log('📦 Loaded from localStorage:', { completedCount: Object.keys(localCompleted).length, historyCount: Object.keys(localHistory).length });
        setCompletedWorkouts(localCompleted);
        setExerciseHistory(localHistory);
      } catch {}
      setIsLoadingData(false);
      return;
    }

    try {
      // Load completed workouts (into component state only - NOT localStorage)
      // localStorage is only written by the explicit "Finish Workout" action
      console.log('📥 Fetching completed workouts...');
      const completedResult = await getCompletedWorkouts(userEmail);
      console.log('✅ Completed workouts result:', completedResult);
      if (completedResult.success) {
        setCompletedWorkouts(completedResult.data);
      }

      // Load exercise history
      console.log('📥 Fetching exercise history...');
      const historyResult = await getExerciseHistoryFromDB(userEmail);
      console.log('✅ Exercise history result:', historyResult);
      if (historyResult.success) {
        console.log('💪 Exercise history data:', historyResult.data);
        console.log('📊 Exercises with history:', Object.keys(historyResult.data));
        setExerciseHistory(historyResult.data);
        // Also save to localStorage as backup
        localStorage.setItem('exerciseHistory', JSON.stringify(historyResult.data));
      } else {
        console.error('❌ Failed to load exercise history:', historyResult.error);
      }
    } catch (error) {
      console.error('❌ Error loading workout data:', error);
      // Fallback to localStorage
      try {
        const localCompleted = JSON.parse(localStorage.getItem('completedWorkouts') || '{}');
        const localHistory = JSON.parse(localStorage.getItem('exerciseHistory') || '{}');
        console.log('📦 Fallback to localStorage:', { completedCount: Object.keys(localCompleted).length, historyCount: Object.keys(localHistory).length });
        setCompletedWorkouts(localCompleted);
        setExerciseHistory(localHistory);
      } catch {}
    } finally {
      setIsLoadingData(false);
    }
  };

  // Screen wake lock to keep phone awake during workout (with iOS fallback)
  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      // Try native Wake Lock API first (Android/Chrome)
      try {
        if ('wakeLock' in navigator && workoutStarted) {
          wakeLock = await navigator.wakeLock.request('screen');
          console.log('Screen wake lock activated');
          return; // Success, no need for fallback
        }
      } catch (err) {
        console.log('Wake lock not supported, using video fallback');
      }
      
      // iOS fallback: play silent video to keep screen awake
      if (workoutStarted && !noSleepVideoRef.current) {
        try {
          const video = document.createElement('video');
          video.setAttribute('playsinline', 'true');
          video.setAttribute('webkit-playsinline', 'true');
          video.setAttribute('muted', 'true');
          video.setAttribute('loop', 'true');
          video.setAttribute('autoplay', 'true');
          video.muted = true; // Set as property too
          video.playsInline = true; // Set as property
          video.style.position = 'fixed';
          video.style.left = '-9999px';
          video.style.top = '-9999px';
          video.style.width = '1px';
          video.style.height = '1px';
          video.style.opacity = '0';
          video.style.pointerEvents = 'none';
          video.src = '/silence.mp4';
          document.body.appendChild(video);
          
          // Try to play immediately
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log('iOS wake lock (video) activated successfully');
              })
              .catch((error) => {
                console.log('Video autoplay prevented, will retry on next user interaction:', error);
                // Retry on next user interaction
                const retryPlay = () => {
                  video.play().then(() => {
                    console.log('iOS wake lock (video) activated after user interaction');
                    document.removeEventListener('touchstart', retryPlay);
                    document.removeEventListener('click', retryPlay);
                  }).catch(() => {});
                };
                document.addEventListener('touchstart', retryPlay, { once: true });
                document.addEventListener('click', retryPlay, { once: true });
              });
          }
          
          noSleepVideoRef.current = video;
        } catch (e) {
          console.log('Video wake lock failed:', e);
        }
      }
    };

    const releaseWakeLock = async () => {
      // Release native wake lock
      if (wakeLock !== null) {
        try {
          await wakeLock.release();
          wakeLock = null;
          console.log('Screen wake lock released');
        } catch (err) {
          console.log('Wake lock release failed:', err);
        }
      }
      
      // Stop and remove video fallback
      if (noSleepVideoRef.current) {
        try {
          noSleepVideoRef.current.pause();
          noSleepVideoRef.current.remove();
          noSleepVideoRef.current = null;
          console.log('iOS wake lock (video) released');
        } catch (e) {}
      }
    };

    if (workoutStarted) {
      requestWakeLock();
    }

    return () => {
      releaseWakeLock();
    };
  }, [workoutStarted]);

  // Workout timer effect
  useEffect(() => {
    let interval;
    if (workoutStarted && workoutStartTime) {
      interval = setInterval(() => {
        setWorkoutDuration(Math.floor((Date.now() - workoutStartTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [workoutStarted, workoutStartTime]);

  // Rest timer effect with background persistence using Page Visibility API
  useEffect(() => {
    let interval;
    
    const handleVisibilityChange = () => {
      if (document.hidden && restTimerActive && restTimerEndTime) {
        localStorage.setItem('restTimerEndTime', restTimerEndTime.toString());
      } else if (!document.hidden && restTimerActive) {
        const storedEndTime = localStorage.getItem('restTimerEndTime');
        if (storedEndTime) {
          const endTime = parseInt(storedEndTime, 10);
          const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
          setRestTimeRemaining(remaining);
          if (remaining === 0) {
            setRestTimerActive(false);
            // playTimerSound called by the separate effect below
          }
          localStorage.removeItem('restTimerEndTime');
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    if (restTimerActive && restTimeRemaining > 0) {
      interval = setInterval(() => {
        setRestTimeRemaining(prev => {
          if (prev <= 1) {
            setRestTimerActive(false);
            setRestTimerEndTime(null);
            localStorage.removeItem('restTimerEndTime');
            // Don't call playTimerSound here — setState inside setState doesn't re-render
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [restTimerActive, restTimeRemaining, restTimerEndTime]);

  // Separate effect: fire playTimerSound when timer reaches 0
  const prevRestTimeRef = useRef(restTimeRemaining);
  const timerSoundPlayedRef = useRef(false);
  useEffect(() => {
    // Timer just hit 0 (was previously > 0) — guard against double-fire
    // Also skip if user explicitly pressed Skip
    if (restTimeRemaining === 0 && prevRestTimeRef.current > 0 && !timerSoundPlayedRef.current && !timerSkippedRef.current) {
      timerSoundPlayedRef.current = true;
      playTimerSound();
    }
    if (restTimeRemaining > 0) {
      timerSoundPlayedRef.current = false; // Reset for next timer
    }
    prevRestTimeRef.current = restTimeRemaining;
  }, [restTimeRemaining, playTimerSound]);

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Parse rest time from string (e.g., "60s", "90s", "2min")
  const parseRestTime = (restStr) => {
    if (!restStr) return 60;
    const str = String(restStr).toLowerCase();
    const match = str.match(/(\d+)\s*(s|sec|second|seconds|m|min|minute|minutes)?/);
    if (match) {
      const value = parseInt(match[1], 10);
      const unit = match[2];
      if (unit && (unit.startsWith('m') || unit === 'm')) {
        return value * 60;
      }
      return value;
    }
    return 60;
  };

  // Start workout
  const startWorkout = () => {
    setWorkoutStarted(true);
    setWorkoutStartTime(Date.now());
  };

  // Get previous best weight for an exercise - lookup by NAME
  const getPreviousBest = (exerciseName) => {
    const history = exerciseHistory[exerciseName];
    if (!history || history.length === 0) return null;
    
    // Find max weight
    const maxWeight = Math.max(...history.map(entry => parseFloat(entry.weight) || 0));
    return maxWeight > 0 ? maxWeight : null;
  };
  
  // Check if current weight is a PR
  const checkForPR = (exerciseId, exerciseName, weight) => {
    const previousBest = getPreviousBest(exerciseName);
    const currentWeight = parseFloat(weight);
    
    if (currentWeight > 0 && (!previousBest || currentWeight > previousBest)) {
      return {
        exerciseId,
        exerciseName,
        weight: currentWeight,
        previousBest,
        isFirstTime: !previousBest
      };
    }
    return null;
  };
  
  // Save exercise to history (both localStorage and Supabase)
  const saveExerciseToHistory = async (exerciseId, exerciseName, weight, reps) => {
    const newEntry = {
      weight: parseFloat(weight) || 0,
      reps: parseInt(reps) || 0,
      date: new Date().toISOString(),
      unit: weightUnit
    };
    
    // Save by exercise NAME instead of ID so it works across workout versions
    const newHistory = {
      ...exerciseHistory,
      [exerciseName]: [
        ...(exerciseHistory[exerciseName] || []),
        newEntry
      ]
    };
    setExerciseHistory(newHistory);
    localStorage.setItem('exerciseHistory', JSON.stringify(newHistory));
    
    // Save to Supabase
    const userEmail = localStorage.getItem('userEmail');
    console.log(`💾 Attempting to save: ${exerciseName}`, { weight, reps, userEmail: !!userEmail });
    
    if (!userEmail) {
      console.warn('⚠️ No user email - skipping Supabase save');
      return;
    }
    
    if (!weight || parseFloat(weight) <= 0) {
      console.warn(`⚠️ Invalid weight (${weight}) - skipping Supabase save`);
      return;
    }
    
    // Allow reps to be 0 or empty for timed exercises
    try {
      console.log(`📤 Saving to Supabase: ${exerciseName} - ${weight}${weightUnit} x ${reps || 0} reps`);
      await saveExerciseProgress(userEmail, {
        exercise_id: exerciseId,
        exercise_name: exerciseName,
        workout_id: activeWorkout?.id || 'unknown',
        set_number: (exerciseHistory[exerciseName] || []).length + 1,
        weight: parseFloat(weight) || 0,
        weight_unit: weightUnit,
        reps: parseInt(reps) || 0,
        is_pr: false // Will be updated if it's a PR
      });
      console.log(`✅ Saved to Supabase successfully: ${exerciseName}`);
    } catch (error) {
      console.error(`❌ Error saving ${exerciseName} to Supabase:`, error);
    }
  };
  
  // Complete set
  const completeSet = async (exerciseId, setIndex, weight, reps) => {
    const key = `${exerciseId}_${setIndex}`;
    const isCompleting = !completedSets[key];
    
    const newCompletedSets = {
      ...completedSets,
      [key]: isCompleting
    };
    
    setCompletedSets(newCompletedSets);

    // If completing the set, save to history and check for PR
    if (isCompleting) {
      const data = setData[key];
      const exercise = activeWorkout?.exercises?.find(ex => ex.id === exerciseId);
      const setConfig = exercise?.sets?.[setIndex];
      
      console.log(`✓ Completing set: ${exercise?.name}, set ${setIndex + 1}`, { 
        data, 
        setConfig,
        weightFromData: data?.weight,
        repsFromData: data?.reps,
        repsFromConfig: setConfig?.reps 
      });
      
      if (data && data.weight && exercise) {
        // Use reps from data if entered, otherwise use pre-set reps from workout config
        const repsToSave = data.reps || setConfig?.reps || 0;
        console.log(`💾 Saving: ${exercise.name} - ${data.weight}kg x ${repsToSave} reps`);
        
        // Save to history
        saveExerciseToHistory(exerciseId, exercise.name, data.weight, repsToSave);
        
        // Check for PR
        const pr = checkForPR(exerciseId, exercise.name, data.weight);
        if (pr) {
          setPersonalBests(prev => {
            // Avoid duplicates
            const exists = prev.some(p => p.exerciseId === pr.exerciseId);
            if (exists) {
              return prev.map(p => p.exerciseId === pr.exerciseId ? pr : p);
            }
            return [...prev, pr];
          });
        }
      }
      
      // Check if all sets for this exercise are now completed
      const sets = Array.isArray(exercise?.sets) ? exercise.sets : [];
      const allSetsCompleted = sets.every((_, idx) => {
        const setKey = `${exerciseId}_${idx}`;
        return newCompletedSets[setKey] === true;
      });

      // Auto-advance to next exercise if all sets completed
      if (allSetsCompleted && activeWorkout?.exercises) {
        const currentIndex = activeWorkout.exercises.findIndex(ex => ex.id === exerciseId);
        const nextIndex = currentIndex + 1;
        
        if (nextIndex < activeWorkout.exercises.length) {
          // Delay slightly so user sees the completion
          setTimeout(() => {
            // Scroll to next exercise smoothly
            const nextExerciseElement = document.getElementById(`exercise-${nextIndex}`);
            if (nextExerciseElement) {
              nextExerciseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 800);
        }
      }
      
      // Start rest timer for all sets (including last set)
      if (exercise && exercise.restTime) {
        const restSeconds = parseRestTime(exercise.restTime);
        const endTime = Date.now() + (restSeconds * 1000);
        const timerId = `timer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        timerSkippedRef.current = false;
        currentTimerIdRef.current = timerId;
        setRestTimeRemaining(restSeconds);
        setRestTimerEndTime(endTime);
        setRestTimerActive(true);

        // Schedule BACKEND PUSH notification (works even when app is closed/locked)
        if (notifPermission === 'granted') {
          console.log('[Rest Timer] Setting up notifications for', restSeconds, 'seconds');
          try {
            subscribeToPush()
              .then(subscription => {
                if (subscription) {
                  console.log('[Rest Timer] ✅ Push subscription active - lock-screen notifications enabled');
                  return schedulePushNotification(restSeconds, 'Rest Over! 💪', 'It\'s time for your next set! Let\'s do this!', timerId);
                } else {
                  console.warn('[Rest Timer] ⚠️ Push subscription unavailable - falling back to local notifications (works when app is open)');
                  return false;
                }
              })
              .then(scheduled => {
                if (scheduled) {
                  console.log('[Rest Timer] ✅ Backend push scheduled successfully');
                } else {
                  console.log('[Rest Timer] Using local notification + visual alert (keep app open for best experience)');
                }
              })
              .catch(err => {
                console.log('[Rest Timer] Push service unavailable, using fallback notifications');
              });
          } catch (e) {
            console.log('[Rest Timer] Using local notification system');
          }
        }
      }
    }
  };

  // Calculate progress
  const calculateProgress = () => {
    if (!activeWorkout?.exercises) return 0;
    let totalSets = 0;
    let completedCount = 0;

    activeWorkout.exercises.forEach(exercise => {
      const sets = Array.isArray(exercise.sets) ? exercise.sets.length : 0;
      totalSets += sets;
      
      for (let i = 0; i < sets; i++) {
        if (completedSets[`${exercise.id}_${i}`]) {
          completedCount++;
        }
      }
    });

    return totalSets > 0 ? (completedCount / totalSets) * 100 : 0;
  };

  // Calculate workout stats for completion summary
  const calculateWorkoutStats = () => {
    let totalWeightLifted = 0;
    let newExercisesTried = 0;

    if (activeWorkout?.exercises) {
      activeWorkout.exercises.forEach(exercise => {
        const exerciseId = exercise.id;
        const sets = Array.isArray(exercise.sets) ? exercise.sets : [];
        
        // Check if this is a new exercise (first time tracked)
        const history = exerciseHistory[exerciseId];
        const isNewExercise = !history || history.length === 0;
        
        sets.forEach((set, idx) => {
          const key = `${exerciseId}_${idx}`;
          if (completedSets[key] && setData[key]) {
            const weight = parseFloat(setData[key].weight) || 0;
            const reps = parseFloat(setData[key].reps) || parseFloat(set.reps) || 0;
            totalWeightLifted += weight * reps;
            
            // Count new exercise only once per exercise
            if (isNewExercise && idx === 0 && weight > 0) {
              newExercisesTried++;
            }
          }
        });
      });
    }

    return { totalWeightLifted, newExercisesTried };
  };

  // Calculate estimated calories burned (matches smartwatch algorithms)
  const estimateCalories = (durationMinutes, exerciseCount, totalSets) => {
    // Evidence-based MET (Metabolic Equivalent) formula used by Apple Watch, Fitbit, etc.
    // Formula: Calories = (MET × body weight in kg × duration in hours)
    // Source: Compendium of Physical Activities (Ainsworth et al., 2011)
    
    // Estimate average body weight (can be customized later)
    const estimatedBodyWeight = 70; // kg (154 lbs) - average for adults
    
    // MET values for resistance training (research-based):
    // Light intensity (3.5 MET): Bodyweight exercises, light weights
    // Moderate intensity (5.0 MET): General weight lifting, moderate effort
    // Vigorous intensity (6.0 MET): Heavy weights, circuit training, high volume
    
    // Calculate intensity based on workout characteristics
    let MET = 5.0; // Base: moderate intensity
    
    // Adjust MET based on volume (sets per minute indicates intensity)
    const setsPerMinute = totalSets / Math.max(durationMinutes, 1);
    if (setsPerMinute > 0.8) {
      MET = 6.0; // High volume = vigorous intensity
    } else if (setsPerMinute < 0.4) {
      MET = 4.0; // Lower volume = light-moderate intensity
    }
    
    // Adjust MET based on exercise variety (more exercises = higher intensity/complexity)
    if (exerciseCount >= 8) {
      MET += 0.5; // Complex workouts burn more
    }
    
    // Calculate calories using standard formula
    const durationHours = durationMinutes / 60;
    const estimatedCalories = Math.round(MET * estimatedBodyWeight * durationHours);
    
    // Ensure reasonable bounds (smartwatches typically show 150-600 cal for 30-60 min strength training)
    return Math.max(50, Math.min(estimatedCalories, 800));
  };

  // Complete workout (save to both localStorage and Supabase)
  const completeWorkout = async () => {
    setShowFinishConfirm(false);
    setWorkoutStarted(false);
    localStorage.removeItem('workoutInProgress');
    
    // Calculate workout stats
    const totalSets = Object.keys(completedSets).filter(key => completedSets[key]).length;
    
    // Mark workout as complete
    if (activeWorkout?.id) {
      const workoutData = {
        completedAt: new Date().toISOString(),
        duration: workoutDuration,
        progress: isRunWorkout ? 100 : calculateProgress(),
        setData: setData,
        notes: exerciseNotes,
        personalBests: personalBests,
        totalSets: totalSets,
        workoutId: activeWorkout.id,
        workoutTitle: activeWorkout.title,
        workoutType: activeWorkout.workoutType || 'strength',
        runLog: isRunWorkout ? runLog : undefined
      };
      
      // Use unique key per completion (timestamp-based) so repeating a workout counts separately
      // Also keep the workout ID key for backward-compat lookups (e.g. checking if a workout is done)
      const completionKey = `${activeWorkout.id}_${Date.now()}`;
      
      // Read existing localStorage data (only contains explicitly completed workouts)
      // Do NOT spread the full Supabase history (completedWorkouts state) into localStorage
      let existingLocal = {};
      try {
        existingLocal = JSON.parse(localStorage.getItem('completedWorkouts') || '{}');
      } catch {}
      
      const updatedLocal = {
        ...existingLocal,
        [completionKey]: workoutData,
        [activeWorkout.id]: workoutData
      };
      localStorage.setItem('completedWorkouts', JSON.stringify(updatedLocal));
      
      // Update component state with full data (includes Supabase history for internal use)
      setCompletedWorkouts({
        ...completedWorkouts,
        [completionKey]: workoutData,
        [activeWorkout.id]: workoutData
      });
      
      // Save to Supabase (with retry on failure)
      const userEmail = localStorage.getItem('userEmail');
      if (userEmail) {
        const savePayload = {
          workoutTitle: activeWorkout.title,
          programId: programId || null,
          duration: workoutDuration,
          setData: isRunWorkout ? { ...setData, _run: runLog } : setData,
          notes: exerciseNotes,
          personalBests: personalBests,
          weekNumber: weekNumber || null,
          totalSets: totalSets
        };
        
        try {
          const result = await saveCompletedWorkout(userEmail, activeWorkout.id, savePayload);
          if (result.success) {
            console.log('✅ Workout saved to Supabase successfully');
          } else {
            console.warn('⚠️ Supabase save returned error, retrying...', result.error);
            // Retry once after a short delay
            await new Promise(r => setTimeout(r, 1000));
            const retry = await saveCompletedWorkout(userEmail, activeWorkout.id, savePayload);
            if (retry.success) {
              console.log('✅ Workout saved to Supabase on retry');
            } else {
              console.error('❌ Supabase save failed after retry:', retry.error);
            }
          }
        } catch (error) {
          console.error('❌ Error saving workout to Supabase:', error);
          // Retry once
          try {
            await new Promise(r => setTimeout(r, 1000));
            await saveCompletedWorkout(userEmail, activeWorkout.id, savePayload);
            console.log('✅ Workout saved to Supabase on retry');
          } catch (retryError) {
            console.error('❌ Supabase retry also failed:', retryError);
          }
        }
      }
      
      // Dispatch event to trigger stats refresh across the app
      window.dispatchEvent(new CustomEvent('workoutCompleted', {
        detail: {
          workoutId: activeWorkout.id,
          workoutTitle: activeWorkout.title,
          duration: workoutDuration,
          programId: programId,
          timestamp: Date.now()
        }
      }));
    }
    
    setShowCompletionModal(true);
  };

  // Close completion modal and reset
  const closeCompletionModal = () => {
    localStorage.removeItem('workoutInProgress');
    setShowCompletionModal(false);
    setActiveWorkout(null);
    setCompletedSets({});
    setSetData({});
    setExerciseNotes({});
    setPersonalBests([]);
    
    // Navigate back to the program/week view if we have that info
    if (programId && weekNumber) {
      navigate('/workouts', { 
        state: { 
          returnToProgram: programId, 
          returnToWeek: weekNumber 
        } 
      });
    } else {
      navigate('/workouts');
    }
  };

  // Sample workout data for testing
  const sampleWorkout = {
    id: 'test-workout-1',
    title: 'Day 1 – Glutes & Hamstrings',
    duration: '45 min',
    exercises: [
      {
        id: 'ex-1',
        name: 'Hip Circles (Warm-up)',
        sets: [
          { setNumber: 1, reps: '12', type: 'warmup', isTimed: false },
          { setNumber: 2, reps: '12', type: 'warmup', isTimed: false }
        ],
        restTime: '30s',
        videoUrl: 'https://www.youtube.com/watch?v=example1',
        instructions: 'Stand on one leg, make large circles with the other leg',
        muscleGroups: ['Hips', 'Glutes']
      },
      {
        id: 'ex-2',
        name: 'Hip Thrust',
        sets: [
          { setNumber: 1, reps: '10', type: 'warmup', isTimed: false, previousWeight: 95 },
          { setNumber: 2, reps: '10', type: 'normal', isTimed: false, previousWeight: 135 },
          { setNumber: 3, reps: '10', type: 'normal', isTimed: false, previousWeight: 135 },
          { setNumber: 4, reps: '10', type: 'dropset', isTimed: false, previousWeight: 135 }
        ],
        restTime: '90s',
        supersetGroup: 'A',
        videoUrl: 'https://www.youtube.com/watch?v=example2',
        instructions: 'Position upper back on bench, drive through heels',
        muscleGroups: ['Glutes', 'Hamstrings']
      },
      {
        id: 'ex-3',
        name: 'Walking Lunges',
        sets: [
          { setNumber: 1, reps: '12', type: 'normal', isTimed: false, previousWeight: 25 },
          { setNumber: 2, reps: '12', type: 'normal', isTimed: false, previousWeight: 25 },
          { setNumber: 3, reps: '12', type: 'normal', isTimed: false, previousWeight: 25 },
          { setNumber: 4, reps: '12', type: 'failure', isTimed: false, previousWeight: 25 }
        ],
        restTime: '90s',
        supersetGroup: 'A',
        videoUrl: 'https://youtube.com',
        instructions: 'Step forward, lower back knee, alternate legs',
        muscleGroups: ['Quads', 'Glutes']
      },
      {
        id: 'ex-4',
        name: 'Romanian Deadlift',
        sets: [
          { setNumber: 1, reps: '12', type: 'normal', isTimed: false, previousWeight: 95 },
          { setNumber: 2, reps: '12', type: 'normal', isTimed: false, previousWeight: 95 },
          { setNumber: 3, reps: '12', type: 'normal', isTimed: false, previousWeight: 95 }
        ],
        restTime: '60s',
        videoUrl: 'https://youtube.com',
        instructions: 'Hinge at hips, keep back straight, lower weights',
        muscleGroups: ['Hamstrings', 'Lower Back']
      },
      {
        id: 'ex-5',
        name: '30 min Stairmaster',
        sets: [
          { setNumber: 1, reps: '30 mins', type: 'normal', isTimed: true }
        ],
        restTime: '0s',
        videoUrl: 'https://youtube.com',
        instructions: 'Maintain steady pace, keep posture upright',
        muscleGroups: ['Cardio', 'Glutes', 'Legs']
      }
    ]
  };

  // Show loading state while fetching data
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-sage-50 pt-20 px-4 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-charcoal-800 mx-auto mb-4"></div>
          <p className="text-charcoal-600">Loading your workout data...</p>
        </div>
      </div>
    );
  }

  // If no workout selected, show program selector
  if (!activeWorkout) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-sage-50 pt-20 px-4 pb-20">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-charcoal-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your Workouts
          </h1>
          <p className="text-charcoal-600 mb-8">Select a program to get started</p>
          
          {/* Test Workout Button */}
          <motion.button
            onClick={() => setActiveWorkout(sampleWorkout)}
            className="w-full bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200 hover:border-coral-400 transition-all text-left"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-bold text-charcoal-800">
                Test Workout - Day 1
              </h3>
              <span className="px-3 py-1 bg-coral-100 text-coral-600 rounded-full text-sm font-semibold">
                New
              </span>
            </div>
            <p className="text-charcoal-600 mb-3">
              Glutes & Hamstrings • 45 min • 5 exercises
            </p>
            <div className="text-sm text-charcoal-500">
              Includes: Warm-up, Supersets, Cardio Finisher
            </div>
          </motion.button>

          <div className="mt-8 text-center text-sm text-charcoal-500">
            👆 Click to test the new workout tracker
          </div>
        </div>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Minimal Header */}
      <div className="fixed top-16 left-0 right-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 z-50">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            onClick={() => {
              if (workoutStarted) {
                if (window.confirm('Are you sure you want to exit? Your progress will be lost.')) {
                  closeCompletionModal();
                }
              } else {
                closeCompletionModal();
              }
            }}
            className="text-charcoal-800 hover:text-charcoal-600 font-medium flex items-center gap-1 px-2 py-1 min-h-[44px]"
          >
            <span className="text-xl">←</span>
            <span className="text-sm">Exit</span>
          </button>
          <div className="text-center flex-1 px-2">
            <h2 className="text-xs sm:text-sm font-medium text-charcoal-800 truncate">{activeWorkout.title}</h2>
            {workoutStarted && (
              <div className="text-xs text-charcoal-500 mt-0.5">{formatTime(workoutDuration)}</div>
            )}
          </div>
          <div className="w-12 sm:w-16"></div>
        </div>
      </div>

      {/* Rest Timer - Floating Center Box */}
      <AnimatePresence>
        {restTimerActive && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 pointer-events-none"
          >
            <div className="bg-gradient-to-br from-charcoal-800 to-charcoal-900 text-white px-5 sm:px-6 py-4 sm:py-5 rounded-2xl shadow-2xl max-w-lg w-full border-2 border-charcoal-700 pointer-events-auto">
              <div className="flex items-center justify-between gap-4">
                {/* Left: Timer Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">⏰</span>
                    <span className="text-xs sm:text-sm uppercase tracking-wider font-semibold text-white">Rest Time</span>
                  </div>
                  <div className="text-4xl sm:text-5xl font-mono font-bold tracking-tight text-white">{formatTime(restTimeRemaining)}</div>
                </div>
                
                {/* Right: Skip Button */}
                <button 
                  onClick={async () => {
                    timerSkippedRef.current = true;
                    setRestTimerActive(false);
                    setRestTimerEndTime(null);
                    setShowRestOverAlert(false);
                    localStorage.removeItem('restTimerEndTime');
                    
                    // Cancel scheduled local notification
                    if (activeNotificationTimeoutRef.current) {
                      clearTimeout(activeNotificationTimeoutRef.current);
                      activeNotificationTimeoutRef.current = null;
                    }
                    
                    // CRITICAL: Tell service worker to block this timer's push notification
                    if (currentTimerIdRef.current) {
                      try {
                        const reg = await navigator.serviceWorker.ready;
                        reg.active?.postMessage({ type: 'CANCEL_TIMER', timerId: currentTimerIdRef.current });
                      } catch (e) {}
                      currentTimerIdRef.current = null;
                    }
                  }} 
                  className="bg-white text-charcoal-800 px-5 sm:px-6 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl min-h-[60px] sm:min-h-[70px] flex items-center justify-center"
                >
                  Skip →
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* REST OVER Visual Alert - Guaranteed to work on every device */}
      <AnimatePresence>
        {showRestOverAlert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center"
            style={{ backgroundColor: '#2C2C2C' }}
          >
            <div className="text-center px-6">
              <motion.div
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                style={{ opacity: 1 }}
              >
                <div style={{ fontSize: '80px', marginBottom: '24px' }}>💪</div>
                <h2 
                  style={{ 
                    fontSize: '56px', 
                    fontWeight: '800', 
                    marginBottom: '16px',
                    color: 'rgb(255, 255, 255)',
                    lineHeight: '1.2',
                    opacity: 1
                  }}
                >
                  Rest Over!
                </h2>
                <p 
                  style={{ 
                    fontSize: '28px', 
                    fontWeight: '600', 
                    marginBottom: '40px',
                    color: 'rgb(255, 255, 255)',
                    lineHeight: '1.4',
                    opacity: 1
                  }}
                >
                  It's time for your next set! Let's do this!
                </p>
                <button
                  onClick={() => setShowRestOverAlert(false)}
                  style={{
                    backgroundColor: 'rgb(255, 255, 255)',
                    color: 'rgb(44, 44, 44)',
                    padding: '20px 48px',
                    borderRadius: '50px',
                    fontSize: '20px',
                    fontWeight: '700',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                    opacity: 1
                  }}
                >
                  Let's Go!
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-20 pb-32">
        {!workoutStarted ? (
          <div className="text-center py-8 sm:py-12">
            <h1 className="text-2xl sm:text-3xl font-light text-charcoal-800 mb-3 px-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {activeWorkout.title}
            </h1>
            <p className="text-charcoal-500 mb-8 text-sm">
              {activeWorkout.duration} • {isRunWorkout ? `🏃 ${describeRun(activeWorkout)}` : `${activeWorkout.exercises?.length || 0} exercises`}
            </p>
            <motion.button
              onClick={startWorkout}
              className="bg-gradient-to-r from-rose-400 to-pink-400 text-white px-8 sm:px-12 py-3.5 rounded-full text-sm font-medium hover:from-rose-500 hover:to-pink-500 transition-colors min-h-[48px]"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Begin Workout
            </motion.button>
          </div>
        ) : (
          <>
            {/* Notification Enable Banner */}
            {notifPermission === 'default' && (
              <div className="mb-6 rounded-2xl p-4 shadow-lg w-full" style={{ backgroundColor: '#4A5568' }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold mb-1" style={{ color: '#FFFFFF' }}>Enable rest timer notifications</p>
                    <p className="text-xs leading-relaxed" style={{ color: '#E2E8F0' }}>
                      Get notified when your rest is over (even with phone locked)
                    </p>
                  </div>
                  <button
                    onClick={requestNotificationPermission}
                    className="px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-colors"
                    style={{ backgroundColor: '#FFFFFF', color: '#2D3748' }}
                  >
                    Enable
                  </button>
                </div>
              </div>
            )}

            {/* Progress Bar & Unit Selector (strength workouts only) */}
            {!isRunWorkout && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wide text-charcoal-500">Progress</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-charcoal-600">{Math.round(progress)}%</span>
                  {/* Unit Selector */}
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setWeightUnit('lbs')}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                        weightUnit === 'lbs'
                          ? 'bg-charcoal-800 text-white'
                          : 'text-charcoal-600 hover:text-charcoal-800'
                      }`}
                    >
                      lbs
                    </button>
                    <button
                      onClick={() => setWeightUnit('kg')}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                        weightUnit === 'kg'
                          ? 'bg-charcoal-800 text-white'
                          : 'text-charcoal-600 hover:text-charcoal-800'
                      }`}
                    >
                      kg
                    </button>
                  </div>
                </div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1">
                <motion.div
                  className="h-1 bg-charcoal-800 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
            )}

            {/* Exercise List / Run Card */}
            {isRunWorkout ? (
              /* RUN WORKOUT VIEW */
              <div className="bg-white rounded-2xl border-2 border-blue-100 shadow-sm p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🏃</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wide">
                    {activeWorkout.runType || 'Run'}
                  </span>
                </div>

                {/* Target */}
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-wide text-charcoal-500 mb-1">Today's Target</div>
                  <div className="text-3xl font-bold text-charcoal-800">{runTargetText(activeWorkout)}</div>
                  {activeWorkout.effort && (
                    <div className="mt-1 text-sm font-medium text-charcoal-600">
                      Effort: {activeWorkout.effort}
                    </div>
                  )}
                  {activeWorkout.targetMode === 'intervals' && activeWorkout.recovery && (
                    <div className="mt-1 text-sm text-charcoal-500">
                      Recovery: {activeWorkout.recovery}
                    </div>
                  )}
                </div>

                {activeWorkout.description && (
                  <p className="text-sm text-charcoal-600 mb-4 leading-relaxed">
                    {activeWorkout.description}
                  </p>
                )}

                {/* Log your run */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-3">
                    Log your run (optional)
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="flex gap-1">
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="Distance"
                        value={runLog.distance}
                        onChange={(e) => setRunLog({ ...runLog, distance: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400"
                      />
                      <select
                        value={runLog.distanceUnit}
                        onChange={(e) => setRunLog({ ...runLog, distanceUnit: e.target.value })}
                        className="border border-gray-200 rounded-lg px-2 py-2.5 text-sm bg-white"
                      >
                        <option value="km">km</option>
                        <option value="mi">mi</option>
                      </select>
                    </div>
                    <select
                      value={runLog.rpe}
                      onChange={(e) => setRunLog({ ...runLog, rpe: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white"
                    >
                      <option value="">How hard? (RPE)</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                        <option key={n} value={n}>RPE {n}{n <= 3 ? ' — Easy' : n <= 5 ? ' — Steady' : n <= 7 ? ' — Hard' : n <= 9 ? ' — Very hard' : ' — Max'}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    placeholder="Notes — how did it feel? (optional)"
                    value={runLog.notes}
                    onChange={(e) => setRunLog({ ...runLog, notes: e.target.value })}
                    rows="2"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400"
                  />
                </div>
              </div>
            ) : (
            <div className="space-y-6">
              {(() => {
                // Group consecutive exercises by superset letter
                const groups = [];
                let i = 0;
                const exercises = activeWorkout.exercises;
                while (i < exercises.length) {
                  const ex = exercises[i];
                  const letter = ex.supersetGroup ? ex.supersetGroup.charAt(0) : null;
                  if (letter) {
                    // Collect all consecutive exercises with the same superset letter
                    const group = [{ exercise: ex, originalIndex: i }];
                    let j = i + 1;
                    while (j < exercises.length && exercises[j].supersetGroup?.charAt(0) === letter) {
                      group.push({ exercise: exercises[j], originalIndex: j });
                      j++;
                    }
                    if (group.length > 1) {
                      groups.push({ type: 'superset', letter, items: group });
                    } else {
                      groups.push({ type: 'single', items: group });
                    }
                    i = j;
                  } else {
                    groups.push({ type: 'single', items: [{ exercise: ex, originalIndex: i }] });
                    i++;
                  }
                }

                return groups.map((group, gIdx) => {
                  if (group.type === 'superset') {
                    return (
                      <div key={`superset-${group.letter}-${gIdx}`} className="relative">
                        {/* Superset label */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" />
                            </svg>
                            Superset {group.letter}
                          </div>
                          <div className="flex-1 h-px bg-purple-200"></div>
                        </div>
                        {/* Superset container with left bar */}
                        <div className="relative pl-4 border-l-[3px] border-purple-400 space-y-0">
                          {group.items.map((item, itemIdx) => (
                            <React.Fragment key={item.exercise.id}>
                              <PremiumExerciseCard
                                exercise={item.exercise}
                                exerciseNumber={item.originalIndex + 1}
                                completedSets={completedSets}
                                setData={setData}
                                exerciseNotes={exerciseNotes}
                                weightUnit={weightUnit}
                                onCompleteSet={completeSet}
                                onUpdateSetData={setSetData}
                                onUpdateNotes={setExerciseNotes}
                              />
                              {/* Chain link connector between superset exercises */}
                              {itemIdx < group.items.length - 1 && (
                                <div className="flex items-center justify-center py-1 relative">
                                  <div className="absolute left-[-0.85rem] w-5 h-5 bg-purple-400 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" />
                                    </svg>
                                  </div>
                                  <span className="text-[10px] font-semibold text-purple-500 bg-purple-50 px-2 py-0.5 rounded">
                                    No rest — go straight into next exercise
                                  </span>
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    );
                  } else {
                    const item = group.items[0];
                    return (
                      <PremiumExerciseCard
                        key={item.exercise.id}
                        exercise={item.exercise}
                        exerciseNumber={item.originalIndex + 1}
                        completedSets={completedSets}
                        setData={setData}
                        exerciseNotes={exerciseNotes}
                        weightUnit={weightUnit}
                        onCompleteSet={completeSet}
                        onUpdateSetData={setSetData}
                        onUpdateNotes={setExerciseNotes}
                      />
                    );
                  }
                });
              })()}
            </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Bar */}
      {workoutStarted && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-6 py-4 z-40">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="text-sm text-charcoal-600">
              {isRunWorkout ? `🏃 ${formatTime(workoutDuration)} elapsed` : `${Math.round(progress)}% Complete`}
            </div>
            <button
              onClick={() => setShowFinishConfirm(true)}
              className="bg-gradient-to-r from-rose-400 to-pink-400 text-white px-8 py-2.5 rounded-full text-sm font-medium hover:from-rose-500 hover:to-pink-500 transition-colors"
            >
              Finish Workout
            </button>
          </div>
        </div>
      )}

      {/* Finish Workout Confirmation Modal */}
      <AnimatePresence>
        {showFinishConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowFinishConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-4xl mb-4">🏁</div>
              <h2 className="text-xl sm:text-2xl font-bold text-charcoal-800 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Finish Workout?
              </h2>
              <p className="text-sm sm:text-base text-charcoal-600 mb-6">
                Are you sure you want to complete this workout? Your progress will be saved.
              </p>
              <div className="space-y-2">
                <button
                  onClick={completeWorkout}
                  className="w-full bg-gradient-to-r from-rose-400 to-pink-400 text-white py-3 rounded-xl font-semibold hover:from-rose-500 hover:to-pink-500 transition-colors"
                >
                  Yes, Finish Workout
                </button>
                <button
                  onClick={() => setShowFinishConfirm(false)}
                  className="w-full bg-gray-100 text-charcoal-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  Keep Going
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completion Modal with PR Celebration */}
      <AnimatePresence>
        {showCompletionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full text-center max-h-[90vh] overflow-y-auto"
            >
              <div className="text-5xl sm:text-6xl mb-4">🎉</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-charcoal-800 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Workout Complete!
              </h2>
              <p className="text-sm sm:text-base text-charcoal-600 mb-2">
                {activeWorkout?.title}
              </p>
              <p className="text-base sm:text-lg font-semibold text-charcoal-700 mb-6">
                Duration: {formatTime(workoutDuration)}
              </p>
              
              {/* Workout Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {isRunWorkout ? (
                  <>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="text-2xl font-bold text-blue-600">
                        {runLog.distance ? `${runLog.distance}` : '—'}
                      </div>
                      <div className="text-xs text-charcoal-600">{runLog.distanceUnit} covered</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <div className="text-2xl font-bold text-green-600">
                        {formatTime(workoutDuration)}
                      </div>
                      <div className="text-xs text-charcoal-600">Duration</div>
                    </div>
                    {runLog.rpe && (
                      <div className="bg-purple-50 rounded-lg p-3 col-span-2">
                        <div className="text-2xl font-bold text-purple-600">RPE {runLog.rpe}/10</div>
                        <div className="text-xs text-charcoal-600">How it felt</div>
                      </div>
                    )}
                  </>
                ) : (() => {
                  const stats = calculateWorkoutStats();
                  const totalSets = Object.keys(completedSets).filter(key => completedSets[key]).length;
                  
                  return (
                    <>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="text-2xl font-bold text-blue-600">
                          {stats.totalWeightLifted.toFixed(0)}
                        </div>
                        <div className="text-xs text-charcoal-600">{weightUnit} Volume</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3">
                        <div className="text-2xl font-bold text-green-600">
                          {totalSets}
                        </div>
                        <div className="text-xs text-charcoal-600">Sets Completed</div>
                      </div>
                      {stats.newExercisesTried > 0 && (
                        <div className="bg-purple-50 rounded-lg p-3">
                          <div className="text-2xl font-bold text-purple-600">
                            {stats.newExercisesTried}
                          </div>
                          <div className="text-xs text-charcoal-600">New Exercise{stats.newExercisesTried > 1 ? 's' : ''}</div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              
              {/* Personal Bests Section */}
              {personalBests.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-300"
                >
                  <div className="text-3xl mb-2">🏆</div>
                  <h3 className="text-xl font-bold text-orange-700 mb-3">
                    Personal Best{personalBests.length > 1 ? 's' : ''}!
                  </h3>
                  <div className="space-y-2">
                    {personalBests.map((pr, idx) => (
                      <motion.div
                        key={pr.exerciseId}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + (idx * 0.1) }}
                        className="bg-white rounded-lg p-3 text-left"
                      >
                        <div className="font-bold text-charcoal-800">{pr.exerciseName}</div>
                        <div className="text-sm text-charcoal-600">
                          {pr.isFirstTime ? (
                            <span>First time tracked: <strong>{pr.weight} {weightUnit}</strong> 🎯</span>
                          ) : (
                            <span>
                              New PR: <strong>{pr.weight} {weightUnit}</strong> 
                              <span className="text-green-600 ml-1">
                                (+{(pr.weight - pr.previousBest).toFixed(1)} {weightUnit})
                              </span>
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
              
              <div className="mb-6">
                <div className="text-4xl font-bold text-green-600 mb-1">
                  {isRunWorkout ? '🏃' : `${Math.round(progress)}%`}
                </div>
                <div className="text-sm text-charcoal-500">{isRunWorkout ? 'Run Complete' : 'Sets Completed'}</div>
              </div>
              
              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  onClick={async () => {
                    const stats = calculateWorkoutStats();
                    const totalSets = Object.keys(completedSets).filter(key => completedSets[key]).length;
                    
                    const shareText = isRunWorkout
                      ? `🏃 Just finished: ${activeWorkout?.title}!\n\n⏱️ ${formatTime(workoutDuration)}${runLog.distance ? `\n� ${runLog.distance} ${runLog.distanceUnit}` : ''}${runLog.rpe ? `\n�💪 RPE ${runLog.rpe}/10` : ''}\n\n#AleraCommunity #FitnessJourney`
                      : `💪 Just crushed: ${activeWorkout?.title}!\n\n⏱️ ${formatTime(workoutDuration)}\n🏋️ ${stats.totalWeightLifted.toFixed(0)} ${weightUnit} lifted\n✅ ${totalSets} sets completed${personalBests.length > 0 ? `\n🏆 ${personalBests.length} PR${personalBests.length > 1 ? 's' : ''}!` : ''}\n\n#AleraCommunity #FitnessJourney`;
                    
                    const shareData = {
                      title: `Workout Complete: ${activeWorkout?.title}`,
                      text: shareText,
                      url: 'https://alera.co.nz'
                    };

                    try {
                      if (navigator.share) {
                        await navigator.share(shareData);
                      } else {
                        await navigator.clipboard.writeText(shareText);
                        alert('Workout stats copied! Paste into Instagram or Facebook 🎉');
                      }
                    } catch (error) {
                      if (error.name !== 'AbortError') {
                        console.error('Error sharing:', error);
                      }
                    }
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-600 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span>Share to Instagram/Facebook</span>
                </button>
                <button
                  onClick={() => {
                    // Prepare workout stats for sharing
                    const stats = calculateWorkoutStats();
                    const totalSets = Object.keys(completedSets).filter(key => completedSets[key]).length;
                    
                    const shareText = isRunWorkout
                      ? `🏃 Just finished: ${activeWorkout?.title}!\n\n⏱️ ${formatTime(workoutDuration)}${runLog.distance ? `\n📏 ${runLog.distance} ${runLog.distanceUnit}` : ''}${runLog.rpe ? `\n💪 RPE ${runLog.rpe}/10` : ''}`
                      : `💪 Just crushed: ${activeWorkout?.title}!\n\n⏱️ ${formatTime(workoutDuration)}\n🏋️ ${stats.totalWeightLifted.toFixed(0)} ${weightUnit} lifted\n✅ ${totalSets} sets completed${personalBests.length > 0 ? `\n🏆 ${personalBests.length} PR${personalBests.length > 1 ? 's' : ''}!` : ''}`;
                    
                    // Store in localStorage for forum to pick up
                    localStorage.setItem('shareWorkoutStats', JSON.stringify({
                      text: shareText,
                      timestamp: Date.now()
                    }));
                    
                    // Navigate to forum
                    window.location.href = '/forum?share=workout';
                  }}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-colors flex items-center justify-center gap-2"
                >
                  <span>💬</span>
                  <span>Share to Community</span>
                </button>
                <button
                  onClick={closeCompletionModal}
                  className="w-full bg-gray-200 text-charcoal-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                >
                  Back to Workouts
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Parse time string to seconds (handles "30 mins", "45s", "60 seconds", "1:30", "90", etc.)
const parseTimeToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const str = String(timeStr).trim().toLowerCase();
  
  // "1:30" format
  if (str.includes(':')) {
    const parts = str.split(':');
    return (parseInt(parts[0]) || 0) * 60 + (parseInt(parts[1]) || 0);
  }
  // "30 mins" or "30 min" or "30 minutes"
  if (str.includes('min')) {
    return (parseInt(str) || 0) * 60;
  }
  // "45s" or "45 sec" or "45 seconds"
  if (str.includes('s')) {
    return parseInt(str) || 0;
  }
  // Plain number — if > 5 assume seconds, otherwise minutes
  const num = parseInt(str) || 0;
  return num > 5 ? num : num * 60;
};

// Format seconds to MM:SS display
const formatTimerDisplay = (totalSeconds) => {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Premium Exercise Card Component
const PremiumExerciseCard = ({ exercise, exerciseNumber, completedSets, setData, exerciseNotes, weightUnit, onCompleteSet, onUpdateSetData, onUpdateNotes }) => {
  const sets = Array.isArray(exercise.sets) ? exercise.sets : [];
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showHistoryGraph, setShowHistoryGraph] = useState(false);
  
  // Timer state for timed exercises — keyed by set index
  const [timers, setTimers] = useState({});
  const timerIntervalsRef = useRef({});
  // Queue for auto-completing sets after timer finishes (avoids calling onCompleteSet inside setState)
  const [pendingAutoComplete, setPendingAutoComplete] = useState(null);

  // Process pending auto-completions outside of setState callbacks
  useEffect(() => {
    if (pendingAutoComplete !== null) {
      const { exerciseId, setIndex: sIdx } = pendingAutoComplete;
      if (!completedSets[`${exerciseId}_${sIdx}`]) {
        onCompleteSet(exerciseId, sIdx);
      }
      setPendingAutoComplete(null);
    }
  }, [pendingAutoComplete]);

  // Cleanup timer intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(timerIntervalsRef.current).forEach(clearInterval);
    };
  }, []);

  // Start countdown timer for a timed set
  const startTimer = (setIndex, totalSeconds) => {
    const key = setIndex;
    // If already running, do nothing
    if (timerIntervalsRef.current[key]) return;
    
    // Initialize timer if not started yet
    setTimers(prev => {
      const existing = prev[key];
      if (!existing || existing.remaining === 0) {
        return { ...prev, [key]: { total: totalSeconds, remaining: totalSeconds, running: true } };
      }
      return { ...prev, [key]: { ...existing, running: true } };
    });

    timerIntervalsRef.current[key] = setInterval(() => {
      setTimers(prev => {
        const timer = prev[key];
        if (!timer || timer.remaining <= 1) {
          clearInterval(timerIntervalsRef.current[key]);
          delete timerIntervalsRef.current[key];
          // Queue auto-complete outside this setState callback
          setPendingAutoComplete({ exerciseId: exercise.id, setIndex });
          return { ...prev, [key]: { ...timer, remaining: 0, running: false } };
        }
        return { ...prev, [key]: { ...timer, remaining: timer.remaining - 1 } };
      });
    }, 1000);
  };

  // Pause timer
  const pauseTimer = (setIndex) => {
    const key = setIndex;
    if (timerIntervalsRef.current[key]) {
      clearInterval(timerIntervalsRef.current[key]);
      delete timerIntervalsRef.current[key];
    }
    setTimers(prev => ({
      ...prev,
      [key]: { ...prev[key], running: false }
    }));
  };

  // Reset timer
  const resetTimer = (setIndex, totalSeconds) => {
    const key = setIndex;
    if (timerIntervalsRef.current[key]) {
      clearInterval(timerIntervalsRef.current[key]);
      delete timerIntervalsRef.current[key];
    }
    setTimers(prev => ({
      ...prev,
      [key]: { total: totalSeconds, remaining: totalSeconds, running: false }
    }));
  };

  // Get exercise history from localStorage
  const exerciseHistory = (() => {
    try {
      return JSON.parse(localStorage.getItem('exerciseHistory') || '{}');
    } catch {
      return {};
    }
  })();
  
  // Get previous best for this exercise - lookup by NAME not ID
  const previousBest = (() => {
    console.log(`🏋️ Exercise: ${exercise.name}, ID: ${exercise.id}`);
    // Look up by exercise name instead of ID
    const history = exerciseHistory[exercise.name];
    console.log(`📜 History for "${exercise.name}":`, history);
    if (!history || history.length === 0) {
      console.log(`❌ No history found for "${exercise.name}"`);
      return null;
    }
    const maxWeight = Math.max(...history.map(entry => parseFloat(entry.weight) || 0));
    console.log(`💪 Previous best for ${exercise.name}: ${maxWeight}`);
    return maxWeight > 0 ? maxWeight : null;
  })();

  // Calculate completion
  const completedCount = sets.filter((_, idx) => completedSets[`${exercise.id}_${idx}`]).length;
  const isComplete = completedCount === sets.length && sets.length > 0;

  // Extract YouTube video ID from URL (supports watch, shorts, live, embed, youtu.be)
  const getYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?.*v=)([^#&?]{11})/,        // watch?v=
      /(?:youtu\.be\/)([^#&?]{11})/,                       // youtu.be/
      /(?:youtube\.com\/embed\/)([^#&?]{11})/,              // embed/
      /(?:youtube\.com\/shorts\/)([^#&?]{11})/,             // shorts/
      /(?:youtube\.com\/live\/)([^#&?]{11})/,               // live/
      /(?:youtube\.com\/v\/)([^#&?]{11})/,                  // v/
      /(?:youtube\.com\/.*[&?]v=)([^#&?]{11})/,             // &v= (fallback)
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const videoId = getYouTubeId(exercise.videoUrl);

  // Handle input change
  const handleInputChange = (setIndex, field, value) => {
    const key = `${exercise.id}_${setIndex}`;
    onUpdateSetData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  // Handle notes change
  const handleNotesChange = (value) => {
    onUpdateNotes(prev => ({
      ...prev,
      [exercise.id]: value
    }));
  };

  return (
    <motion.div
      id={`exercise-${exerciseNumber - 1}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl shadow-sm border-2 transition-all p-4 sm:p-5 ${
        isComplete ? 'border-green-500 bg-green-50/30' : 'border-gray-200'
      }`}
    >
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-charcoal-400">#{exerciseNumber}</span>
            {exercise.supersetGroup && (
              <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded">
                {exercise.supersetGroup}
              </span>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Progress Button - Always visible */}
            <button
              onClick={() => setShowHistoryGraph(!showHistoryGraph)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                showHistoryGraph 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              <span>📈</span>
              <span>{showHistoryGraph ? 'Hide' : 'Progress'}</span>
            </button>
            
            {/* Video Button - Opens Modal */}
            {exercise.videoUrl && (
              <button
                onClick={() => setShowVideoModal(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors"
              >
                <span>📹</span>
                <span>Watch</span>
              </button>
            )}
          </div>
        </div>
        
        <h3 className="text-lg font-medium text-charcoal-800 mb-2">
          {exercise.name}
        </h3>
        
        {/* Exercise History Graph - Expandable */}
        {showHistoryGraph && (() => {
          const history = exerciseHistory[exercise.name] || [];
          const recentHistory = history.slice(-8); // Last 8 entries
          
          // If no history yet, show message
          if (recentHistory.length === 0) {
            return (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border border-gray-200 text-center"
              >
                <div className="text-2xl mb-2">📊</div>
                <p className="text-sm text-charcoal-600">
                  Complete sets with weight to start tracking your progress!
                </p>
              </motion.div>
            );
          }
          
          // If only 1 entry, show it but note more data needed
          if (recentHistory.length === 1) {
            const weight = parseFloat(recentHistory[0].weight) || 0;
            return (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 p-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-blue-700">📈 Progress</span>
                  <span className="text-xs text-charcoal-600">First entry!</span>
                </div>
                <div className="text-center py-2">
                  <div className="text-2xl font-bold text-blue-600">{weight} {weightUnit}</div>
                  <p className="text-xs text-charcoal-600 mt-1">Complete more sets to see your progress graph</p>
                </div>
              </motion.div>
            );
          }
          
          // Show full graph with 2+ entries
          if (recentHistory.length >= 2) {
            const maxWeight = Math.max(...recentHistory.map(h => parseFloat(h.weight) || 0));
            const minWeight = Math.min(...recentHistory.map(h => parseFloat(h.weight) || 0));
            const range = maxWeight - minWeight || 1;
            
            return (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 p-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-blue-700">📈 Progress</span>
                  <span className="text-xs text-charcoal-600">Last {recentHistory.length} sets</span>
                </div>
                <div className="flex items-end gap-1 h-12">
                  {recentHistory.map((entry, idx) => {
                    const weight = parseFloat(entry.weight) || 0;
                    const heightPercent = range > 0 ? ((weight - minWeight) / range) * 100 : 50;
                    const isLatest = idx === recentHistory.length - 1;
                    
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className={`w-full rounded-t transition-all ${
                            isLatest ? 'bg-gradient-to-t from-blue-600 to-purple-600' : 'bg-blue-400'
                          }`}
                          style={{ height: `${Math.max(heightPercent, 10)}%` }}
                        />
                        <span className="text-xs font-medium text-charcoal-700">{weight}</span>
                      </div>
                    );
                  })}
                </div>
                {previousBest && (
                  <div className="mt-2 text-xs text-center">
                    <span className="text-charcoal-600">PR: </span>
                    <span className="font-bold text-blue-700">{previousBest} {weightUnit}</span>
                  </div>
                )}
              </motion.div>
            );
          }
          return null;
        })()}
        
        {/* Instructions */}
        {exercise.instructions && (
          <p className="text-sm text-charcoal-600 leading-relaxed">
            {exercise.instructions}
          </p>
        )}
      </div>

      {/* Sets with Weight/Reps Input */}
      <div className="space-y-2">
        {sets.map((set, setIndex) => {
          const isCompleted = completedSets[`${exercise.id}_${setIndex}`];
          const key = `${exercise.id}_${setIndex}`;
          const data = setData[key] || {};
          
          // Set type badge
          const setTypeConfig = {
            warmup: { label: 'Warm-up', color: 'bg-blue-100 text-blue-700' },
            dropset: { label: 'Drop Set', color: 'bg-orange-100 text-orange-700' },
            failure: { label: 'To Failure', color: 'bg-red-100 text-red-700' },
            normal: { label: '', color: '' }
          };
          const setType = set.type || 'normal';
          const typeInfo = setTypeConfig[setType] || setTypeConfig.normal;
          
          // Pre-fill reps with target
          const targetReps = set.reps || '10';
          const currentReps = data.reps !== undefined ? data.reps : targetReps;
          
          return (
            <div
              key={setIndex}
              className={`border rounded-lg p-3 transition-all ${
                isCompleted
                  ? 'bg-green-50 border-green-300'
                  : 'bg-white border-gray-200'
              }`}
            >
              {set.isTimed ? (() => {
                // TIMED EXERCISE SET — show countdown timer
                const totalSeconds = parseTimeToSeconds(set.reps || '60s');
                const timer = timers[setIndex];
                const remaining = timer ? timer.remaining : totalSeconds;
                const isRunning = timer?.running || false;
                const isFinished = timer && timer.remaining === 0;
                const progress = totalSeconds > 0 ? ((totalSeconds - remaining) / totalSeconds) * 100 : 0;

                return (
                  <div className="flex flex-col items-center gap-3">
                    {/* Set label */}
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-charcoal-800">Set {set.setNumber}</span>
                        {typeInfo.label && (
                          <span className={`text-xs px-2 py-0.5 rounded ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                        )}
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">⏱ Timed</span>
                      </div>
                      <span className="text-xs text-charcoal-500">{set.reps}</span>
                    </div>

                    {/* Timer display */}
                    <div className="relative w-full">
                      {/* Progress bar background */}
                      <div className="w-full h-16 bg-gray-100 rounded-xl overflow-hidden relative">
                        <div 
                          className={`h-full transition-all duration-1000 ease-linear rounded-xl ${
                            isFinished ? 'bg-green-400' : isRunning ? 'bg-gradient-to-r from-amber-400 to-orange-400' : 'bg-gray-200'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className={`text-2xl font-mono font-bold ${
                            isFinished ? 'text-green-700' : isRunning ? 'text-charcoal-800' : 'text-charcoal-600'
                          }`}>
                            {isFinished ? '✓ Done!' : formatTimerDisplay(remaining)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Timer controls */}
                    {!isCompleted && (
                      <div className="flex items-center gap-2 w-full">
                        {!isRunning && !isFinished && (
                          <button
                            onClick={() => startTimer(setIndex, remaining === 0 ? totalSeconds : remaining)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold text-sm hover:from-amber-600 hover:to-orange-600 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            {timer && remaining < totalSeconds ? 'Resume' : 'Start Timer'}
                          </button>
                        )}
                        {isRunning && (
                          <button
                            onClick={() => pauseTimer(setIndex)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gray-600 text-white rounded-xl font-semibold text-sm hover:bg-gray-700 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                            Pause
                          </button>
                        )}
                        {(timer && (remaining < totalSeconds || isFinished)) && !isRunning && (
                          <button
                            onClick={() => resetTimer(setIndex, totalSeconds)}
                            className="px-4 py-2.5 bg-gray-200 text-charcoal-700 rounded-xl text-sm font-medium hover:bg-gray-300 transition-colors"
                          >
                            Reset
                          </button>
                        )}
                        {isFinished && (
                          <button
                            onClick={() => onCompleteSet(exercise.id, setIndex)}
                            className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700 transition-colors"
                          >
                            ✓ Complete
                          </button>
                        )}
                      </div>
                    )}
                    {isCompleted && (
                      <div className="flex items-center gap-2 text-green-600 font-semibold text-sm">
                        <span>✓</span> Completed
                      </div>
                    )}
                  </div>
                );
              })() : (
                // STANDARD SET — weight/reps inputs
                <div className="flex items-center gap-2">
                  {/* Set Number & Type */}
                  <div className="flex flex-col items-center min-w-[60px]">
                    <span className="text-sm font-semibold text-charcoal-800">Set {set.setNumber}</span>
                    {typeInfo.label && (
                      <span className={`text-xs px-2 py-0.5 rounded mt-1 ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                    )}
                  </div>

                  {/* Weight Input */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <label className="text-xs text-charcoal-500">Weight ({weightUnit})</label>
                      {previousBest && !data.weight && (
                        <span className="text-xs text-green-600 font-medium">PR: {previousBest}</span>
                      )}
                    </div>
                    <input
                      type="number"
                      placeholder={previousBest ? previousBest.toString() : "0"}
                      value={data.weight || ''}
                      onChange={(e) => handleInputChange(setIndex, 'weight', e.target.value)}
                      className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-charcoal-800 focus:border-transparent"
                      disabled={isCompleted}
                    />
                  </div>

                  {/* Reps Input (Pre-filled with target) */}
                  <div className="flex-1">
                    <label className="text-xs text-charcoal-500 block mb-1">Reps</label>
                    <input
                      type="number"
                      placeholder={targetReps}
                      value={currentReps}
                      onChange={(e) => handleInputChange(setIndex, 'reps', e.target.value)}
                      className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-charcoal-800 focus:border-transparent"
                      disabled={isCompleted}
                    />
                  </div>

                  {/* Complete Checkbox */}
                  <div className="flex flex-col">
                    <label className="text-xs text-charcoal-500 block mb-1 opacity-0">Done</label>
                    <button
                      onClick={() => onCompleteSet(exercise.id, setIndex)}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                        isCompleted
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-charcoal-600 hover:bg-charcoal-800 hover:text-white'
                      }`}
                    >
                      {isCompleted ? '✓' : '○'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Notes Section */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <label className="text-sm font-medium text-charcoal-700 block mb-2">
          📝 Notes
        </label>
        <textarea
          value={exerciseNotes[exercise.id] || ''}
          onChange={(e) => handleNotesChange(e.target.value)}
          placeholder="Add notes about this exercise (e.g., form cues, how it felt, adjustments needed...)"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-charcoal-800 focus:border-transparent resize-none"
          rows="3"
        />
      </div>

      {/* Completion Badge */}
      {isComplete && (
        <div className="mt-4 text-center">
          <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
            ✓ Complete
          </span>
        </div>
      )}

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && exercise.videoUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl overflow-hidden max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-charcoal-800">{exercise.name}</h3>
                <button
                  onClick={() => setShowVideoModal(false)}
                  className="text-charcoal-600 hover:text-charcoal-800 text-2xl"
                >
                  ✕
                </button>
              </div>
              
              {/* Video Player */}
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                {videoId ? (
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={exercise.name}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-100">
                    <p className="text-charcoal-600">Video URL: {exercise.videoUrl}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WorkoutTrackerV2;
